import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Input } from '@/components/Input';
import { api } from '@/lib/api';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
});

type FormValues = z.infer<typeof schema>;

export function Reset() {
  // Links in emails sent before password links existed land here.
  const expired = useSearchParams()[0].get('expired') === '1';
  const reset = useMutation({
    mutationFn: (input: FormValues) => api.post<void>('/api/Clients/reset', input),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit((values) => reset.mutate(values));

  return (
    <div className="w-full max-w-md">
      <h1 className="font-display text-h1 font-bold">Reset your password</h1>
      <p className="mt-3 text-body text-text-secondary">
        Enter the email associated with your account and we'll send you a reset
        link.
      </p>

      {expired ? (
        <p className="mt-6 text-body-sm text-warning">That link has expired. Request a new one below.</p>
      ) : null}

      {reset.isSuccess ? (
        <div className="mt-10 rounded-md border border-success/40 bg-success/5 px-4 py-3 text-body-sm text-success">
          If an account exists for that email, a reset link has been sent.
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-10 flex flex-col gap-gap-element-lg">
          <Field id="email" label="Email" error={errors.email?.message}>
            <Input id="email" type="email" autoComplete="email" {...register('email')} />
          </Field>

          <Button type="submit" size="lg" disabled={reset.isPending}>
            {reset.isPending ? 'Sending…' : 'Send reset link'}
          </Button>

          <p className="text-body-sm text-text-secondary">
            <Link to="/login" className="text-accent hover:underline">
              Back to sign in
            </Link>
          </p>
        </form>
      )}
    </div>
  );
}
